import type { CSSProperties } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@clarix/ui';
import type { HomeComingSoonContent } from './types';

export interface HomeComingSoonProps extends HomeComingSoonContent {
  className?: string;
}

/**
 * The answer surface for a home that can't act yet.
 *
 * Deliberately NOT a chat reply — asking for something opens a plain
 * announcement. Every default here is app-neutral: no product name, no
 * feature name, no route. The same screen serves every app unchanged.
 */

const DEFAULT_BADGE = 'Coming soon';
const DEFAULT_TITLE = 'This is a work in progress, and it is nearly here.';

/** Staggered entrances are computed, so the delay rides on a CSS variable. */
function delay(ms: number): CSSProperties {
  return { '--home-delay': `${ms}ms` } as CSSProperties;
}

/**
 * Entrance for one block.
 *
 * `backwards` holds the keyframe's 0% state through the delay, so nothing is
 * ever left invisible when the animation doesn't run (reduced motion, a
 * utility that didn't generate) — it degrades to plain static content.
 *
 * Only ever ONE animation utility per element: a second would replace this
 * one outright, since both set the `animation` shorthand.
 */
const ENTER =
  'animate-content-in [animation-delay:var(--home-delay)] [animation-fill-mode:backwards]';

export function HomeComingSoon({
  badge = DEFAULT_BADGE,
  title = DEFAULT_TITLE,
  actionLabel,
  onAction,
  className,
}: HomeComingSoonProps) {
  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-center justify-center overflow-hidden px-6 py-10 text-center',
        className,
      )}
    >
      {/* Soft drifting glow behind the panel — decorative, token-tinted. */}
      <span
        aria-hidden
        className="from-primary/20 animate-float-slow pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[420px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br via-transparent to-transparent blur-3xl"
      />

      <div className={cn(ENTER, 'mb-7')} style={delay(0)}>
        <span className="border-primary/25 bg-primary/[0.04] text-primary inline-flex items-center rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em]">
          {badge}
        </span>
      </div>

      {/* The accent runs through the headline. No sweeping sheen here: the
          overlay would be a rectangle spanning two ragged lines, and against
          the tinted wash it reads as a pale box rather than a highlight. The
          drifting glow above carries the motion instead. */}
      <div className={cn(ENTER)} style={delay(90)}>
        <h2 className="from-foreground via-primary to-foreground max-w-[18ch] bg-gradient-to-r bg-clip-text text-3xl font-bold leading-[1.2] tracking-tight text-transparent sm:max-w-[24ch] sm:text-[2.15rem]">
          {title}
        </h2>
      </div>

      {actionLabel && onAction ? (
        <div className={cn(ENTER, 'mt-9')} style={delay(180)}>
          <button
            type="button"
            onClick={onAction}
            className="border-primary/40 text-primary hover:border-primary group inline-flex items-center gap-2 border-b pb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))]/30"
          >
            {actionLabel}
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={1.5}
            />
          </button>
        </div>
      ) : null}
    </div>
  );
}
